import clsx from 'clsx';
import { Fragment } from 'react';

import DataCellRenderer from '@/App/tiles/TargetAccumulator/Schedule/DataCellRenderer.tsx';
import PropertyHeader from '@/App/tiles/TargetAccumulator/Schedule/PropertyHeader';
import TotalLeverage from '@/App/tiles/TargetAccumulator/Schedule/TotalLeverage.tsx';
import TotalNotional from '@/App/tiles/TargetAccumulator/Schedule/TotalNotional.tsx';
import { useTargetAccumulatorTileId } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabZoomedTileId } from '@/store/tabs/slice.ts';
import {
  selectTargetAccumulatorAccuType,
  selectTargetAccumulatorCurrencyData,
  selectTargetAccumulatorOrderedScheduleItemIds,
} from '@/store/targetAccumulator/selectors.ts';
import { getTargetAccumulatorCalendarColumns } from '@/store/targetAccumulator/utils.ts';

const HEIGHT_SMALL = 142;
const HEIGHT_MEDIUM = 380;

type Props = {
  keepSmall?: boolean;
};

const SchedulerTable = ({ keepSmall = false }: Props) => {
  const tileId = useTargetAccumulatorTileId();
  const accuType = useAppSelector((state) => selectTargetAccumulatorAccuType(state, tileId));
  const { currency } = useAppSelector((state) => selectTargetAccumulatorCurrencyData(state, tileId));

  const scheduleIds = useAppSelector((state) => selectTargetAccumulatorOrderedScheduleItemIds(state, tileId));

  const scheduleColumns = getTargetAccumulatorCalendarColumns(accuType ?? 'TargetAccu');
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;

  return (
    <div
      className="mt-2 overflow-auto"
      style={{
        maxHeight: keepSmall ? `${HEIGHT_SMALL}px` : isZoomed ? 'auto' : `${HEIGHT_MEDIUM}px`,
        flexGrow: 1,
      }}
    >
      <table data-e2e="scheduler" className="table table-sm table-bordered bg-lvl1 mb-0">
        <thead data-e2e="header">
          <tr className="sticky-top">
            <th className="text-nowrap bg-lvl1 py-0 border-start-0 border-bottom-0 position-sticky fs-12">#</th>
            {scheduleColumns.map((property, index) => (
              <th
                className={clsx('text-nowrap bg-lvl1 py-0 border-bottom-0 position-sticky fs-12', {
                  'border-end-0': index === scheduleColumns.length - 1,
                })}
                key={property}
              >
                <PropertyHeader property={property} currency={currency} />
              </th>
            ))}
          </tr>
        </thead>

        <tbody data-e2e="body">
          {scheduleIds?.map((legId, itemIndex) => (
            <tr key={legId} data-e2e="row" className="fs-12">
              <td className="p-0 border-0">
                <div className="d-flex align-items-center px-2 form-control-sm form-control-plaintext text-nowrap">
                  {itemIndex + 1}
                </div>
              </td>

              <Fragment key={legId}>
                {scheduleColumns.map((property, index) => (
                  <td
                    key={`${property}-${legId}`}
                    className={clsx('p-0', { 'border-end-0': index === scheduleColumns.length - 1 })}
                  >
                    <DataCellRenderer property={property} legId={legId} />
                  </td>
                ))}
              </Fragment>
            </tr>
          ))}
        </tbody>

        <tfoot className="sticky-bottom">
          <tr
          //style={{ borderLeftColor: 'white' }}
          >
            <td className="fs-12 bg-lvl1 border-bottom-0 text-end border-start-0" colSpan={scheduleColumns.length - 2}>
              Total
            </td>
            <td className="bg-lvl1 border-bottom-0 text-end">
              <TotalNotional />
            </td>
            <td className="bg-lvl1 border-bottom-0 text-end">
              <TotalLeverage />
            </td>
            <td className="bg-lvl1 border-bottom-0 border-end-0" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SchedulerTable;
